import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Drawer,
  Typography
} from '@mui/material';
import Button from 'components/Common/Button';
import { IoClose } from 'react-icons/io5';
import { useAddColumn, useGetColumns } from 'apis/business/agent/worksheet/hook';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa6';
import { IoChevronDownOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { useGetIntegrations } from 'apis/business/setting/integrations/hook';
import { useMemo } from 'react';

export default function WorkflowForm() {
  const navigate = useNavigate();
  const { data: columnsData } = useGetColumns({ include: 'cells,workflow' });
  const { mutateAsync: addColumn, isPending } = useAddColumn();
  const { data: integrations } = useGetIntegrations();

  const textColumns = useMemo(
    () => columnsData?.data?.filter((column) => column.data_type === 'text') ?? [],
    [columnsData?.data]
  );

  const { handleSubmit, control, watch } = useForm<any>({
    defaultValues: {
      column: {
        workflow_attributes: {
          workflow_details: {
            http_method: 'get'
          }
        }
      }
    }
  });

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: 'column.workflow_attributes.workflow_details.headers'
  });

  const {
    fields: bodyParamsFields,
    append: bodyParamsAppend,
    remove: bodyParamsRemove
  } = useFieldArray({
    control,
    name: 'column.workflow_attributes.workflow_details.body_params'
  });

  const {
    fields: queryParamsFields,
    append: queryParamsAppend,
    remove: queryParamsRemove
  } = useFieldArray({
    control,
    name: 'column.workflow_attributes.workflow_details.query_params'
  });

  const onSubmit = async (data: any) => {
    data.column.data_type = 'app_execution';
    await addColumn(data);
    console.log(data);
  };

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600, lg: 700 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>Configure your Workflow Column</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          <Select
            name='column.workflow_attributes.workflow_type'
            label='Workflow'
            control={control}
            options={[
              { value: 'custom_api', label: 'Custom Api' },
              { value: 'draft_text', label: 'Draft Text' }
            ]}
          />
          <Typography fontSize='12px' mb={1.5} color='secondary'>
            Select what type of workflow you need
          </Typography>
          {watch('column.workflow_attributes.workflow_type') === 'custom_api' && (
            <>
              <Input
                name='column.workflow_attributes.workflow_details.exposed_parameters'
                label='Exposed Parameters'
                placeholder='eg: order_status, dispatched_at'
                control={control}
                sx={{ marginTop: 2 }}
                fullWidth
              />
              <Box my={2}>
                <Select
                  control={control}
                  name='column.workflow_attributes.workflow_details.write_to_column'
                  label='Write to Column'
                  options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                />
              </Box>
              <Input
                name='column.workflow_attributes.workflow_details.base_url'
                label='Base Url'
                control={control}
                sx={{
                  marginTop: 2,
                  '.MuiFormControl-root': {
                    borderRight: '1px solid #cecece'
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position='start'
                        sx={{
                          '.MuiInputBase-root': {
                            marginTop: '10px',
                            marginRight: '10px'
                          }
                        }}
                      >
                        <Select
                          control={control}
                          name='column.workflow_attributes.workflow_details.http_method'
                          label=''
                          options={[
                            { label: 'GET', value: 'get', selected: true },
                            { label: 'POST', value: 'post' },
                            { label: 'PUT', value: 'put' }
                          ]}
                          defaultValue='get'
                          variant='standard'
                          disableUnderline
                          sx={{
                            minWidth: 80
                          }}
                        />
                      </InputAdornment>
                    )
                  }
                }}
                fullWidth
              />
              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #F3F3F3',
                  marginTop: 2,
                  '::before': { background: 'transparent' }
                }}
              >
                <AccordionSummary
                  expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                  aria-controls='panel1-content'
                  id='panel1-header'
                >
                  <Box display='flex' flexDirection='column'>
                    <Typography variant='h6' fontSize={16} fontWeight={600}>
                      Headers
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {headerFields.map((field: any, index: number) => (
                    <Box
                      key={field.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        mb: 3
                      }}
                    >
                      <Input
                        name={`column.workflow_attributes.workflow_details.headers[${index}].field_name`}
                        label='Field name'
                        control={control}
                        sx={{ flex: 2 }}
                        fullWidth
                      />
                      <Input
                        name={`column.workflow_attributes.workflow_details.headers[${index}].value`}
                        label='Value'
                        control={control}
                        sx={{ flex: 2 }}
                        fullWidth
                      />
                      <IconButton color='error' onClick={() => headerRemove(index)}>
                        <MdDelete />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    type='button'
                    color='secondary'
                    size='small'
                    variant='outlined'
                    startIcon={<FaPlus />}
                    onClick={() => headerAppend({ field_name: '', value: '' })}
                    sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                  >
                    Add Field
                  </Button>
                </AccordionDetails>
              </Accordion>
              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #F3F3F3',
                  marginTop: 2,
                  '::before': { background: 'transparent' }
                }}
              >
                <AccordionSummary
                  expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                  aria-controls='panel1-content'
                  id='panel1-header'
                >
                  <Box display='flex' flexDirection='column'>
                    <Typography variant='h6' fontSize={16} fontWeight={600}>
                      Body Params
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {bodyParamsFields.map((field: any, index: number) => (
                    <Grid container spacing={2} alignItems='center' key={index}>
                      <Grid size={5.5}>
                        <Input
                          name={`column.workflow_attributes.workflow_details.body_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={5.5}>
                        <Box>
                          <Select
                            control={control}
                            name={`column.workflow_attributes.workflow_details.body_params[${index}].value`}
                            label='Read from Column'
                            options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                          />
                        </Box>
                      </Grid>
                      <Grid size={1}>
                        <IconButton color='error' onClick={() => bodyParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    type='button'
                    color='secondary'
                    size='small'
                    variant='outlined'
                    startIcon={<FaPlus />}
                    onClick={() => bodyParamsAppend({ field_name: '', column_id: '' })}
                    sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                  >
                    Add Field
                  </Button>
                </AccordionDetails>
              </Accordion>
              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #F3F3F3',
                  marginTop: 2,
                  '::before': { background: 'transparent' }
                }}
              >
                <AccordionSummary
                  expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                  aria-controls='panel1-content'
                  id='panel1-header'
                >
                  <Box display='flex' flexDirection='column'>
                    <Typography variant='h6' fontSize={16} fontWeight={600}>
                      Query Params
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {queryParamsFields.map((field: any, index: number) => (
                    <Grid container spacing={2} alignItems='center' key={index}>
                      <Grid size={5.5}>
                        <Input
                          name={`column.workflow_attributes.workflow_details.query_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={5.5}>
                        <Box>
                          <Select
                            control={control}
                            name={`column.workflow_attributes.workflow_details.query_params[${index}].value`}
                            label='Read from Column'
                            options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                          />
                        </Box>
                      </Grid>
                      <Grid size={1}>
                        <IconButton color='error' onClick={() => queryParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    type='button'
                    color='secondary'
                    size='small'
                    variant='outlined'
                    startIcon={<FaPlus />}
                    onClick={() => queryParamsAppend({ field_name: '', column_id: '' })}
                    sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                  >
                    Add Field
                  </Button>
                </AccordionDetails>
              </Accordion>
            </>
          )}
          {watch('column.workflow_attributes.workflow_type') === 'send_response' && (
            <>
              <Box mt={2}>
                <Select
                  control={control}
                  name='column.workflow_attributes.workflow_details.read_from_column'
                  label='Read from Column'
                  options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                />
              </Box>
              <Box mt={2}>
                <Select
                  control={control}
                  name='column.workflow_attributes.workflow_details.send_to'
                  label='Send To'
                  options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                />
              </Box>
              <Box mt={2}>
                <Select
                  control={control}
                  name='column.workflow_attributes.workflow_details.respond_via'
                  label='Response Via'
                  options={integrations?.data?.map((item) => ({ label: item.pretty_name, value: item.id })) ?? []}
                />
              </Box>
            </>
          )}
          {watch('column.workflow_attributes.workflow_type') === 'draft_text' && (
            <>
              <Input
                name='column.workflow_attributes.workflow_details.prompt'
                label='Prompt'
                control={control}
                fullWidth
                sx={{ marginTop: 2 }}
                multiline
                minRows={4}
              />
              <Typography mt={2} variant='subtitle1'>
                Data Collect For
              </Typography>
              <Box display='flex' flexWrap='wrap' flexDirection='row'>
                {textColumns.length ? (
                  textColumns.map((column) => (
                    <Box key={column.id}>
                      <Controller
                        name='column.workflow_attributes.workflow_details.data_collect_from'
                        control={control}
                        defaultValue={[]}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={column.id}
                                checked={Array.isArray(field.value) && field.value.includes(column.id)}
                                onChange={(e) => {
                                  const value = column.id;
                                  if (e.target.checked) {
                                    field.onChange([...(field.value || []), value]);
                                  } else {
                                    field.onChange((field.value || []).filter((v: any) => v !== value));
                                  }
                                }}
                              />
                            }
                            label={column.name}
                          />
                        )}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant='body2' color='textSecondary'>
                    No columns are added
                  </Typography>
                )}
              </Box>
              <Box mt={2}>
                <Select
                  control={control}
                  name='column.workflow_attributes.workflow_details.add_to_column'
                  label='Add to Column'
                  options={columnsData?.data?.map((item) => ({ label: item.name, value: item.id })) ?? []}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: 2,
            flexShrink: 0
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{ border: '1px solid #F3F3F3' }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            Create
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
